import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { SeasonArchive } from "@averagejoesff/shared";
import { fetchManifest, fetchSeason } from "./api/awards";
import { useAuth } from "./auth/AuthProvider";
import { Layout } from "./components/Layout";
import { SeasonPicker } from "./components/SeasonPicker";
import { ResourceStatus } from "./components/ResourceStatus";
import { StateMessage } from "./components/StateMessage";
import { AdminPanel } from "./features/admin/AdminPanel";
import { CreateSeasonForm } from "./features/admin/CreateSeasonForm";
import {
  DEFAULT_FEATURE_ID,
  NAV_GROUPS,
  SEASON_FEATURES,
  SITE_WIDE_FEATURES,
  activeNavPage,
  featureIdForPath,
} from "./features/registry";
import type { FeatureId, SeasonScope } from "./features/registry";
import { formatGeneratedAt } from "./lib/format";
import { LeagueDocsProvider } from "./lib/LeagueDocsProvider";
import { navigate, useRoute } from "./lib/router";
import { useResource } from "./lib/useResource";

export default function App() {
  const auth = useAuth();
  const pathname = useRoute();
  const activeFeature = featureIdForPath(pathname) ?? DEFAULT_FEATURE_ID;
  const activeNav = activeNavPage(pathname);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Normalizes an unrecognized path (e.g. a stale link) to the home page,
  // so the URL bar always matches what's rendered. CloudFront already
  // serves index.html with a 200 for any unknown path (SPA deep-link
  // fallback — see infra/cloudfront.tf), so this is the only place that
  // has to make sense of it client-side.
  useEffect(() => {
    if (featureIdForPath(pathname) === null) {
      navigate("/", { replace: true });
    }
  }, [pathname]);

  const [manifest] = useResource(fetchManifest, [refreshKey]);

  const currentSeason =
    manifest.status === "ready" ? (manifest.data?.currentSeason ?? null) : null;

  const activeSeasonFeature = SEASON_FEATURES.find((f) => f.id === activeFeature);
  const seasonScope: SeasonScope = activeSeasonFeature?.seasonScope ?? "all";

  // The seasons the active feature lets the user browse — e.g. Dues only
  // ever shows the current season (no picker at all), Prize Payouts is
  // floored at a fixed year, Biggest Blowout allows anything published.
  const availableSeasons = (() => {
    const all = manifest.status === "ready" ? (manifest.data?.seasons ?? []) : [];
    if (seasonScope === "current") return currentSeason !== null ? [currentSeason] : [];
    if (seasonScope === "all") return all;
    return all.filter((s) => s >= seasonScope.minSeason);
  })();

  // Sticky across features when still valid (e.g. picking 2024 on Blowout,
  // then switching to Payouts keeps 2024); otherwise falls back to the
  // current season, then the most recent one this feature supports.
  const season =
    selectedSeason !== null && availableSeasons.includes(selectedSeason)
      ? selectedSeason
      : currentSeason !== null && availableSeasons.includes(currentSeason)
        ? currentSeason
        : (availableSeasons.at(-1) ?? null); // manifest.seasons is ascending

  // Not keyed on refreshKey: every season write (a week's result, the
  // roster, a clear) returns the recomputed archive, which AdminPanel's
  // onSaved pushes straight in via setArchive instead of re-fetching, since a
  // re-fetch this soon after a write would likely hit the CDN's cached
  // pre-write copy (see useResource's doc comment). Only `season` changing
  // (the season picker) should trigger a real re-fetch here.
  const [archive, setArchive] = useResource(
    () => (season === null ? Promise.resolve(null) : fetchSeason(season)),
    [season],
    // Switching seasons keeps the old one on screen (dimmed) until the new one arrives, rather than blanking the page.
    { keepPrevious: true },
  );

  const leagueName =
    manifest.status === "ready" && manifest.data ? manifest.data.leagueName : "Average Joe's FF";

  // Names the page in tabs, history, bookmarks and home-screen shortcuts,
  // which otherwise all read the index.html <title> whatever the path.
  const pageLabel = activeNav?.page.title ?? activeNav?.page.label;
  useEffect(() => {
    document.title = pageLabel ? `${pageLabel} · ${leagueName}` : leagueName;
  }, [pageLabel, leagueName]);

  const footer = useMemo(() => {
    if (manifest.status !== "ready" || !manifest.data) return null;
    // The source is credited by the footer's Yahoo attribution line above this one.
    return <>Data updated {formatGeneratedAt(manifest.data.generatedAt)}</>;
  }, [manifest]);

  /** Bumps the manifest fetch — only needed after a brand-new season is created. */
  function refreshManifest() {
    setRefreshKey((k) => k + 1);
  }

  function accountSlot(closeDrawer: () => void) {
    return auth.isAuthenticated ? (
      <>
        <span className="nav-drawer__account-email">{auth.email}</span>
        {season !== null && (
          <button
            type="button"
            className="nav-drawer__account-action nav-drawer__account-action--accent"
            onClick={() => {
              setEditMode((v) => !v);
              closeDrawer();
            }}
          >
            {editMode ? "Done Editing" : "Edit Season"}
          </button>
        )}
        <button type="button" className="nav-drawer__account-action" onClick={auth.logout}>
          Sign Out
        </button>
      </>
    ) : (
      <button
        type="button"
        className="nav-drawer__account-action nav-drawer__account-action--accent"
        onClick={auth.login}
        disabled={auth.isLoading}
      >
        Sign In
      </button>
    );
  }

  const siteWideFeature = SITE_WIDE_FEATURES.find((f) => f.id === activeFeature);

  // No picker for a "current"-scoped feature (e.g. Dues) — see `season` above.
  const seasonPicker =
    seasonScope !== "current" && availableSeasons.length > 0 && season !== null ? (
      <SeasonPicker seasons={availableSeasons} value={season} onChange={setSelectedSeason} />
    ) : null;

  // Above every page, so the admin-edited documents views share stay one copy
  // across views and page switches (see LeagueDocsProvider).
  return (
    <LeagueDocsProvider season={season}>
      <Layout
        leagueName={leagueName}
        groups={NAV_GROUPS}
        active={activeNav}
        onNavigate={navigate}
        accountSlot={accountSlot}
        accountEmail={auth.email}
        footer={footer}
      >
        {auth.error && (
          <StateMessage variant="error" title="Login failed" detail={auth.error} />
        )}

        {siteWideFeature ? (
          siteWideFeature.render()
        ) : (
          <>
            <ResourceStatus
              resource={manifest}
              loading="Loading league data…"
              error="Could not load the league"
            />

            {manifest.status === "ready" && manifest.data === null && (
              auth.isAuthenticated && auth.idToken ? (
                <CreateSeasonForm
                  idToken={auth.idToken}
                  onCreated={(createdSeason) => {
                    setSelectedSeason(createdSeason);
                    refreshManifest();
                  }}
                />
              ) : (
                <StateMessage
                  variant="empty"
                  title="No results published yet"
                  detail="Check back once the league commissioner publishes this season."
                />
              )
            )}

            {manifest.status === "ready" && manifest.data !== null && season !== null && (
              <>
                {/* Once the feature itself is ready, the picker moves onto the
                    same line as that feature's own intro (see
                    `renderSeasonFeature`) instead of sitting in this
                    standalone row — there's nothing to align it with while
                    loading/erroring/empty, so it stays here for those states. */}
                {!(archive.status === "ready" && archive.data !== null) && (
                  <div className="toolbar">{seasonPicker}</div>
                )}

                <ResourceStatus
                  resource={archive}
                  loading={`Loading ${season}…`}
                  error={`No data for the ${season} season`}
                />
                {archive.status === "ready" && archive.data === null && (
                  <StateMessage
                    variant="empty"
                    title={`No data for the ${season} season`}
                    detail="This season hasn't been published yet."
                  />
                )}
                {archive.status === "ready" && archive.data !== null && (
                  <>
                    {/* Not while refreshing: `archive.data` is then the previous season's, and a save would write it to the new one. */}
                    {editMode && auth.idToken && !archive.refreshing && (
                      <AdminPanel
                        season={season}
                        archive={archive.data}
                        idToken={auth.idToken}
                        onSaved={setArchive}
                      />
                    )}
                    <div className={archive.refreshing ? "is-refreshing" : undefined} aria-busy={archive.refreshing}>
                      {renderSeasonFeature(activeFeature, archive.data, seasonPicker)}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Layout>
    </LeagueDocsProvider>
  );
}

function renderSeasonFeature(feature: FeatureId, archive: SeasonArchive, toolbar: ReactNode) {
  return SEASON_FEATURES.find((f) => f.id === feature)?.render(archive, toolbar) ?? null;
}
